import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button';
import { TextField } from '../../components/TextField';
import { errorMessage } from '../../i18n/errors';
import { useAuth } from './AuthContext';
import styles from './LoginPage.module.css';

/** Anmeldeseite: Benutzername und Passwort, Fehlermeldungen lokalisiert, Rücksprung zum Ziel. */
export function LoginPage() {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const target = (location.state as { from?: string } | null)?.from ?? '/';

  if (user) return <Navigate to={target} replace />;

  /**
   * Sendet die Anmeldedaten; bei Erfolg wird zum Ziel navigiert, sonst die Fehlermeldung gezeigt.
   * @param event Absenden des Formulars.
   */
  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(username, password);
      navigate(target, { replace: true });
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={styles.card}>
      <h1>{t('auth.loginTitle')}</h1>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <TextField
          label={t('auth.username')}
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoComplete="username"
          required
        />
        <TextField
          label={t('auth.password')}
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />
        {error && (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        )}
        <Button type="submit" disabled={busy || !username || !password}>
          {busy ? t('auth.loggingIn') : t('auth.login')}
        </Button>
      </form>
    </section>
  );
}
